import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { CheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const pricingPlans = [{
  name: 'Standard',
  monthlyPrice: '99€',
  annualPrice: '950€',
  period: '/mois',
  description: 'Idéal pour les PME débutant sur les marchés africains',
  features: ['Accès à 10 pays', '5 alertes personnalisées', 'Données historiques (1 an)', 'Support par email', 'Mises à jour quotidiennes', 'Export des données (limité)'],
  cta: "Commencer l'essai gratuit",
  popular: false,
  color: 'blue'
}, {
  name: 'Pro',
  monthlyPrice: '249€',
  annualPrice: '2390€',
  period: '/mois',
  description: 'Pour les entreprises en pleine expansion en Afrique',
  features: ['Accès à 20 pays', '20 alertes personnalisées', 'Données historiques (5 ans)', 'Support prioritaire', 'Rapports sectoriels mensuels', 'Export des données illimité', 'API access (limité)'],
  cta: "Commencer l'essai gratuit",
  popular: true,
  color: 'yellow'
}, {
  name: 'Premium Afrique',
  monthlyPrice: '499€',
  annualPrice: '4790€',
  period: '/mois',
  description: 'Solution complète pour une présence panafricaine',
  features: ['Accès à tous les pays', 'Alertes illimitées', 'Données historiques complètes', 'Support dédié 24/7', 'Coaching personnalisé', 'Analyse de concurrence', 'API access complet', 'Intégration CRM'],
  cta: 'Contacter un conseiller',
  popular: false,
  color: 'blue'
}];
export function PricingSection() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annually'>('monthly');
  // Function to calculate savings amount
  const calculateSavings = (monthlyPrice: string, annualPrice: string) => {
    const monthly = parseInt(monthlyPrice.replace('€', ''));
    const annual = parseInt(annualPrice.replace('€', ''));
    return (monthly * 12 - annual).toFixed(0);
  };
  return <section id="pricing" className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden" data-id="element-592">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-100 to-transparent" data-id="element-593"></div>
      <div className="absolute top-20 right-20 w-64 h-64 bg-yellow-100 rounded-full opacity-30 blur-3xl" data-id="element-594"></div>
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-blue-100 rounded-full opacity-30 blur-3xl" data-id="element-595"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-596">
        <div className="text-center mb-16" data-id="element-597">
          <span className="inline-block px-3 py-1 bg-blue-50 text-blue-900 text-sm font-semibold rounded-full mb-4" data-id="element-598">
            Tarifs
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-599">
            Nos formules d'abonnement
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-600">
            Choisissez l'offre qui correspond le mieux à vos besoins et à votre
            stratégie de développement en Afrique
          </p>
          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center bg-gray-100 p-1 rounded-full" data-id="element-601">
            <button className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${billingPeriod === 'monthly' ? 'bg-white text-blue-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setBillingPeriod('monthly')} data-id="element-602">
              Mensuel
            </button>
            <button className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${billingPeriod === 'annually' ? 'bg-white text-blue-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setBillingPeriod('annually')} data-id="element-603">
              Annuel <span className="text-green-500 font-semibold" data-id="element-604">-20%</span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-605">
          {pricingPlans.map((plan, index) => <motion.div key={plan.name} className={`bg-white rounded-2xl shadow-xl overflow-hidden border ${plan.popular ? 'border-yellow-500 md:scale-105 relative z-10' : 'border-gray-100'}`} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.2
        }} viewport={{
          once: true
        }} data-id="element-606">
              {plan.popular && <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-blue-900 py-2 px-4 text-center text-sm font-semibold" data-id="element-607">
                  RECOMMANDÉ
                </div>}
              <div className="p-8" data-id="element-608">
                <h3 className="text-2xl font-bold text-blue-900 mb-2" data-id="element-609">
                  {plan.name}
                </h3>
                <div className="flex items-baseline mb-2" data-id="element-610">
                  <span className="text-4xl font-bold text-blue-900" data-id="element-611">
                    {billingPeriod === 'monthly' ? plan.monthlyPrice : plan.annualPrice}
                  </span>
                  <span className="text-gray-600 ml-1" data-id="element-612">
                    {billingPeriod === 'monthly' ? '/mois' : '/an'}
                  </span>
                </div>
                {billingPeriod === 'annually' && <div className="mb-4" data-id="element-613">
                    <span className="text-sm text-gray-500" data-id="element-614">
                      Soit {(parseInt(plan.annualPrice) / 12).toFixed(2)}€/mois
                    </span>
                    <div className="text-sm text-green-600 mt-1" data-id="element-615">
                      Économisez{' '}
                      {calculateSavings(plan.monthlyPrice, plan.annualPrice)}€
                      par an
                    </div>
                  </div>}
                <p className="text-gray-600 mb-8 h-12" data-id="element-616">{plan.description}</p>
                {plan.name === 'Premium Afrique' ? <Link to="/parler-conseiller" data-id="element-617">
                    <Button variant={plan.popular ? 'primary' : 'outline'} fullWidth className={plan.popular ? 'shadow-lg shadow-yellow-500/20' : ''} data-id="element-618">
                      {plan.cta}
                    </Button>
                  </Link> : <Button variant={plan.popular ? 'primary' : 'outline'} fullWidth className={plan.popular ? 'shadow-lg shadow-yellow-500/20' : ''} onClick={() => window.location.href = '/essai-gratuit'} data-id="element-619">
                    {plan.cta}
                  </Button>}
                <div className="mt-8 pt-8 border-t border-gray-100" data-id="element-620">
                  <p className="font-medium text-blue-900 mb-4" data-id="element-621">
                    Ce qui est inclus :
                  </p>
                  <ul className="space-y-4" data-id="element-622">
                    {plan.features.map((feature, i) => <li key={i} className="flex items-start" data-id="element-623">
                        <div className={`w-5 h-5 rounded-full ${plan.popular ? 'bg-yellow-100' : 'bg-blue-50'} flex items-center justify-center mr-3 mt-0.5 flex-shrink-0`} data-id="element-624">
                          <CheckIcon size={12} className={plan.popular ? 'text-yellow-600' : 'text-blue-600'} data-id="element-625" />
                        </div>
                        <span className="text-gray-700" data-id="element-626">{feature}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.div>)}
        </div>
        <div className="text-center" data-id="element-627">
          <p className="text-gray-600 mb-6 text-lg" data-id="element-628">
            Besoin d'une solution sur mesure pour votre entreprise ?
          </p>
          <Link to="/parler-conseiller" data-id="element-629">
            <Button variant="secondary" size="lg" className="shadow-lg shadow-blue-900/10" data-id="element-630">
              Demander un devis personnalisé
            </Button>
          </Link>
          <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-4 text-sm text-gray-500" data-id="element-631">
            <div className="flex items-center" data-id="element-632">
              <CheckIcon size={16} className="text-green-500 mr-2" data-id="element-633" />
              Essai gratuit de 7 jours
            </div>
            <div className="flex items-center" data-id="element-634">
              <CheckIcon size={16} className="text-green-500 mr-2" data-id="element-635" />
              Aucun engagement
            </div>
            <div className="flex items-center" data-id="element-636">
              <CheckIcon size={16} className="text-green-500 mr-2" data-id="element-637" />
              Annulation à tout moment
            </div>
          </div>
        </div>
      </div>
    </section>;
}